import { z } from "zod";
import { attendanceCopy } from "@/features/attendance/copy";
import { sharedCopy } from "@/shared/copy";

const attendanceSource = z.enum(["self", "manager", "kiosk"]);

/** One interval of work. Append-only: a correction is a new row pointing at the one it replaces. */
const attendanceEntry = z.object({
  id: z.string(),
  personId: z.string(),
  /** The company's own date at check-in, `YYYY-MM-DD`. Never recomputed from a later zone. */
  day: z.string(),
  checkedInAt: z.iso.datetime(),
  checkedOutAt: z.iso.datetime().nullable(),
  source: attendanceSource,
  /** Who pressed the button or typed the time; null only on rows the removed kiosk wrote. */
  recordedById: z.string().nullable(),
  correctsEntryId: z.string().nullable(),
  /** Closed by the midnight close and not yet given a check-out: it counts 0 h until set. */
  autoClosed: z.boolean(),
  updatedAt: z.iso.datetime(),
});

export const missedCheckOutInput = z.object({
  checkedOutAt: z.iso.datetime({
    error: attendanceCopy.refused.checkOutTime,
  }),
});

const attendanceRow = z.object({
  personId: z.string(),
  name: z.string(),
  photoKey: z.string().nullable(),
  /** Absent on a colleague's row an Employee reads: they see whether somebody is in, not when. */
  entries: z.array(attendanceEntry).optional(),
  /** The day holds nothing. Whether that is absent, a holiday or off is `statusFor`'s to decide. */
  absent: z.boolean(),
});

/** `off` is a day nobody works, which draws no chip at all. */
const attendanceStatus = z.enum([
  "present",
  "on_leave",
  "absent",
  "holiday",
  "off",
]);

/** A calendar day, `YYYY-MM-DD`. The repository checks one too: an agent tool is a second caller. */
export const attendanceDay = z.iso.date({
  error: sharedCopy.refused.notADate,
});

export const attendanceSearch = z.object({ day: attendanceDay });

/** What Who's in counts on Team · Today, each opening Today narrowed to it. */
const dayShown = z.enum(["in", "notInYet", "onLeave"]);

/** No day means today in the company's zone, which this schema cannot know. */
export const attendanceListSearch = z.object({
  day: z.iso.date().optional().catch(undefined),
  show: dayShown.optional().catch(undefined),
});

/** No `absent`: that word is about one day, and this row is about thirty of them. */
const attendanceMonthRow = z.object({
  personId: z.string(),
  name: z.string(),
  entries: z.array(attendanceEntry),
  /** Nothing is asked of somebody before this day, or after `leftAt`: they were not here. */
  startsOn: z.iso.date(),
  leftAt: z.iso.datetime().nullable(),
  /** Days that had a missed check-out, set since or not, which the team month lists. */
  missedDays: z.array(z.string()),
});

/** A calendar month, `YYYY-MM`. The repository checks one too: an agent tool is a second caller. */
export const attendanceMonth = z
  .string()
  .regex(/^\d{4}-(0[1-9]|1[0-2])$/, attendanceCopy.refused.notAMonth);

export const monthSearch = z.object({ month: attendanceMonth });

/** A person's own month carries only which month; unreadable falls back to the current one. */
export const myMonthSearch = z.object({
  month: attendanceMonth.optional().catch(undefined),
});

/**
 * Unreadable falls back to the company's current month, which only its zone decides. The open day
 * panel lives here too, so a link to it opens it.
 */
export const teamMonthSearch = z.object({
  month: attendanceMonth.optional().catch(undefined),
  person: z.string().max(64).optional().catch(undefined),
  day: z.iso.date().optional().catch(undefined),
});

export const historySearch = z.object({
  person: z.string().max(64).default("me"),
  from: attendanceDay,
  to: attendanceDay,
});

export const dayHistorySearch = z.object({
  person: z.string().max(64).default("me"),
  day: attendanceDay,
});

/** One write to a day's times, read from the audit. The action is the screen's to put in words. */
export interface DayChange {
  readonly action: string;
  /** Null when no person made it: the midnight close. */
  readonly byPersonId: string | null;
  readonly at: string;
  readonly checkedInAt: string | null;
  readonly checkedOutAt: string | null;
}

/** The times a manager says the day had; a person's own button is timed by the server. */
export const correctionInput = z
  .object({
    checkedInAt: z.iso.datetime().optional(),
    checkedOutAt: z.iso.datetime().optional(),
  })
  .refine(
    (times) =>
      times.checkedInAt !== undefined || times.checkedOutAt !== undefined,
    {
      message: attendanceCopy.refused.nothingToCorrect,
    },
  );

export type AttendanceEntry = z.infer<typeof attendanceEntry>;
export type AttendanceMonthRow = z.infer<typeof attendanceMonthRow>;
export type AttendanceRow = z.infer<typeof attendanceRow>;
export type TeamMonthSearch = z.infer<typeof teamMonthSearch>;
export type AttendanceStatus = z.infer<typeof attendanceStatus>;
export type DayShown = z.infer<typeof dayShown>;
export type AttendanceSource = z.infer<typeof attendanceSource>;
export type CorrectionInput = z.infer<typeof correctionInput>;
